import { useCallback, useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/** How often to poll for SW updates (ms) */
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000; // 1 hour

/**
 * Service worker state interface
 */
interface ServiceWorkerState {
  readonly needRefresh: boolean;
  readonly offlineReady: boolean;
  readonly updateServiceWorker: (reloadPage?: boolean) => Promise<void>;
}

/**
 * Custom hook to manage service worker registration and updates.
 *
 * Provides state for:
 * - needRefresh: New version available
 * - offlineReady: App ready for offline use
 * - updateServiceWorker: Function to apply updates
 *
 * Checks for updates periodically and when the tab regains focus.
 *
 * @returns Service worker state and update function
 *
 * @example
 * ```tsx
 * function App() {
 *   const { needRefresh, offlineReady, updateServiceWorker } = useServiceWorker();
 *
 *   if (needRefresh) {
 *     return <UpdatePrompt onUpdate={() => updateServiceWorker(true)} />;
 *   }
 * }
 * ```
 */
export function useServiceWorker(): ServiceWorkerState {
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(
    undefined
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined
  );

  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      if (import.meta.env.DEV) {
        console.log('[SW] Service worker registered:', registration);
      }
      registrationRef.current = registration;
    },
    onRegisterError(error) {
      console.error('[SW] Service worker registration failed:', error);
    },
  });

  const checkForUpdate = useCallback(() => {
    registrationRef.current?.update().catch((error: unknown) => {
      console.error('[SW] Update check failed:', error);
    });
  }, []);

  // Periodic update checks + visibilitychange listener
  useEffect(() => {
    intervalRef.current = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate]);

  return {
    needRefresh,
    offlineReady,
    updateServiceWorker,
  };
}
